import catalogData from '../../../data/styles.json';
import type { Language } from '../i18n/types';

export type HairLength = 'short' | 'medium' | 'long';
export type HairVibe = 'classic' | 'modern' | 'messy' | 'disconnected';
export type LocalizedValue<T> = Record<Language, T>;

interface CatalogEntry {
  id: string;
  name: LocalizedValue<string>;
  description: LocalizedValue<string>;
  title: string;
  prompt_hint: string;
  preview_image?: string;
  length?: HairLength;
  vibe?: HairVibe;
  variations?: LocalizedValue<string[]>;
}

export interface HairstyleConfig {
  id: string;
  name: LocalizedValue<string>;
  description: LocalizedValue<string>;
  tags: LocalizedValue<string[]>;
  variations: LocalizedValue<string[]>;
  promptHint: string;
  previewImage?: string;
  length?: HairLength;
  vibe?: HairVibe;
  hiddenInCatalog?: boolean;
  title: string;
  label: string;
  shortDescription: string;
}

export interface LocalizedHairstyleCopy {
  name: string;
  description: string;
  tags: string[];
  variations: string[];
}

const emptyLocalizedList: LocalizedValue<string[]> = { ru: [], en: [], az: [] };

export const HAIRSTYLE_CONFIGS: HairstyleConfig[] = (catalogData as CatalogEntry[]).map((entry) => ({
  id: entry.id,
  name: entry.name,
  description: entry.description,
  tags: emptyLocalizedList,
  variations: entry.variations || emptyLocalizedList,
  promptHint: entry.prompt_hint,
  previewImage: entry.preview_image,
  length: entry.length,
  vibe: entry.vibe,
  title: entry.name.ru,
  label: entry.name.en,
  shortDescription: entry.description.ru,
}));

export const STYLE_ID_MAP: Record<string, string> = {
  buzz_cut: 'buzz',
  curly: 'curly_volume',
  'curly-volume': 'curly_volume',
  curlyvolume: 'curly_volume',
  flow: 'flow_cut',
  flow_cat: 'flow_cut',
  fringe_cat: 'fringe',
};

export function normalizeHairstyleId(id?: string | null): string {
  const normalized = String(id || '').trim().toLowerCase().replace(/\s+/g, '_');
  return STYLE_ID_MAP[normalized] || normalized;
}

export function getHairstyleConfig(id?: string | null): HairstyleConfig | undefined {
  const normalized = normalizeHairstyleId(id);
  return HAIRSTYLE_CONFIGS.find((style) => style.id === normalized);
}

export function getLocalizedHairstyle(style: HairstyleConfig, language: Language): LocalizedHairstyleCopy {
  return {
    name: style.name[language] || style.name.ru,
    description: style.description[language] || style.description.ru,
    tags: style.tags[language] || style.tags.ru,
    variations: style.variations[language] || style.variations.ru,
  };
}

export const QUICK_STYLE_IDS = ['fade', 'textured_crop', 'side_part', 'quiff', 'flow_cut', 'crop'];
